import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { marketingI18n } from './i18n';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { runMarketingReport } from '../../services/marketingReportService';
import CardStats from '../../components/CardStats/CardStats';
import loaderSpinnerAnimation from 'assets/images/loader-spinner.json';
import './marketingAnalysis.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend
);

const HISTORY_KEY = 'marketing_analysis_history_v2';
const LEGACY_HISTORY_KEY = 'marketing_analysis_history_v1';
const MAX_HISTORY = 12;
const DEFAULT_MAX_ROWS = 120;
const FALLBACK_TIMEZONE = 'America/Santiago';

const getQuickPrompts = (t) => [
  t('mk_quickPrompt1'),
  t('mk_quickPrompt2'),
  t('mk_quickPrompt3'),
  t('mk_quickPrompt4'),
];

const SECTION_MATCHERS = {
  summary: ['direct answer', 'resumen', 'summary', 'respuesta directa'],
  insights: ['key insights', 'insights', 'hallazgos'],
  metrics: ['metrics', 'metricas', 'métricas'],
  evidence: ['evidence', 'ejemplos', 'examples', 'evidencia'],
  recommendations: ['recommended actions', 'recomendaciones', 'acciones'],
  confidence: ['confidence', 'confianza'],
  limitations: ['data limitations', 'limitaciones', 'limitations'],
  steps: ['steps', 'plan', 'next steps', 'pasos'],
};

const CHART_THEME = {
  primary: '#126BF5',
  border: '#DEE2E6',
  text: '#212529',
};

const CHART_PALETTE = ['#126BF5', '#3B82F6', '#60A5FA', '#93C5FD', '#ADB5BD'];

const LOTTIE_SCRIPT_SRC =
  'https://unpkg.com/lottie-web@5.12.2/build/player/lottie.min.js';
let lottieScriptPromise;

const loadLottiePlayer = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.lottie) return Promise.resolve(window.lottie);
  if (!lottieScriptPromise) {
    lottieScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = LOTTIE_SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve(window.lottie || null);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }
  return lottieScriptPromise;
};

const normalizePrompt = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const createHistoryId = () =>
  `mk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const parseJsonValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return null;
  }
};

const toNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const normalized = value
      .trim()
      .replace('%', '')
      .replace(/\s+/g, '')
      .replace(',', '.')
      .replace(/[^\d.-]/g, '');

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeChartType = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

  if (normalized === 'doughnut') return 'donut';
  if (normalized === 'stackedbar') return 'stacked_bar';
  return normalized;
};

const createChartOptions = ({ stacked = false, donut = false } = {}) => {
  const base = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          color: CHART_THEME.text,
        },
      },
      tooltip: {
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        footerColor: '#FFFFFF',
      },
    },
  };

  if (donut) return base;

  return {
    ...base,
    scales: {
      x: {
        stacked,
        grid: {
          color: CHART_THEME.border,
        },
        border: {
          color: CHART_THEME.border,
        },
        ticks: {
          color: CHART_THEME.text,
        },
      },
      y: {
        stacked,
        beginAtZero: true,
        grid: {
          color: CHART_THEME.border,
        },
        border: {
          color: CHART_THEME.border,
        },
        ticks: {
          color: CHART_THEME.text,
        },
      },
    },
  };
};

const buildChartModel = (component, t) => {
  const dataRows = Array.isArray(component?.data) ? component.data : [];
  if (!dataRows.length) return null;

  const chartType = normalizeChartType(
    component?.chart_type || component?.chartType || 'bar'
  );
  const labels = dataRows.map((row, index) =>
    String(row?.label ?? row?.group ?? row?.series ?? `Item ${index + 1}`)
  );
  const values = dataRows.map((row) =>
    toNumber(row?.value ?? row?.percentage ?? 0)
  );
  const hasValues = values.some((value) => value !== 0);

  if (!hasValues) return null;

  if (chartType === 'line') {
    return {
      chartType: 'line',
      data: {
        labels,
        datasets: [
          {
            label: component?.title || t('mk_chartDefaultLabel'),
            data: values,
            borderColor: CHART_THEME.primary,
            backgroundColor: CHART_THEME.primary,
            pointRadius: 3,
            pointHoverRadius: 4,
            tension: 0.25,
          },
        ],
      },
      options: createChartOptions(),
    };
  }

  if (chartType === 'donut') {
    return {
      chartType: 'donut',
      data: {
        labels,
        datasets: [
          {
            label: component?.title || t('mk_chartDefaultLabel'),
            data: values,
            backgroundColor: labels.map(
              (_, index) => CHART_PALETTE[index % CHART_PALETTE.length]
            ),
            borderColor: CHART_THEME.border,
            borderWidth: 1,
          },
        ],
      },
      options: createChartOptions({ donut: true }),
    };
  }

  if (chartType === 'stacked_bar') {
    const hasSeries = dataRows.some((row) => row?.series);
    if (hasSeries) {
      const stackedLabels = Array.from(
        new Set(dataRows.map((row) => String(row?.group ?? row?.label ?? '')))
      );
      const seriesList = Array.from(
        new Set(dataRows.map((row) => String(row?.series || 'Serie')))
      );

      const datasets = seriesList.map((seriesName, seriesIndex) => ({
        label: seriesName,
        data: stackedLabels.map((label) => {
          const row = dataRows.find(
            (item) =>
              String(item?.series || 'Serie') === seriesName &&
              String(item?.group ?? item?.label ?? '') === label
          );
          return toNumber(row?.value ?? 0);
        }),
        backgroundColor: CHART_PALETTE[seriesIndex % CHART_PALETTE.length],
        borderColor: CHART_THEME.border,
        borderWidth: 1,
      }));

      return {
        chartType: 'bar',
        data: {
          labels: stackedLabels,
          datasets,
        },
        options: createChartOptions({ stacked: true }),
      };
    }
  }

  const barColors =
    values.length > 1
      ? labels.map((_, index) => CHART_PALETTE[index % CHART_PALETTE.length])
      : CHART_THEME.primary;

  return {
    chartType: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: component?.title || t('mk_chartDefaultLabel'),
          data: values,
          backgroundColor: barColors,
          borderColor: barColors,
          borderWidth: 1,
        },
      ],
    },
    options: createChartOptions({ stacked: chartType === 'stacked_bar' }),
  };
};

const InlineChart = ({ component, t }) => {
  const chartModel = buildChartModel(component, t);

  if (!chartModel) {
    return (
      <div className='marketing-inline-chart-empty'>
        <span className='caption'>{t('mk_chartEmpty')}</span>
      </div>
    );
  }

  return (
    <div className='marketing-inline-chart'>
      {chartModel.chartType === 'line' ? (
        <Line data={chartModel.data} options={chartModel.options} />
      ) : null}
      {chartModel.chartType === 'donut' ? (
        <Doughnut data={chartModel.data} options={chartModel.options} />
      ) : null}
      {chartModel.chartType === 'bar' ? (
        <Bar data={chartModel.data} options={chartModel.options} />
      ) : null}
    </div>
  );
};

const getClientTimeZone = () => {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
    return FALLBACK_TIMEZONE;
  }

  return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
};

const normalizeMaxRows = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_MAX_ROWS;
  return Math.min(500, Math.max(1, Math.floor(parsed)));
};

const toPrettyLabel = (value) =>
  String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const cleanInlineMarkdown = (value) =>
  String(value || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();

const stripNumericPrefix = (value) =>
  String(value || '')
    .replace(/^\s*\d+[.)]\s*/, '')
    .trim();

const toListFromMarkdownSection = (content) => {
  const lines = String(content || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return lines
    .filter((line) => /^[-*]\s+/.test(line) || /^\d+[.)]\s+/.test(line))
    .map((line) => line.replace(/^[-*]\s+/, '').replace(/^\d+[.)]\s+/, ''))
    .map(cleanInlineMarkdown)
    .filter(Boolean);
};

const parseMarkdownSections = (raw, t) => {
  const text = String(raw || '').trim();
  if (!text) return [];

  const sections = [];
  const regex = /(?:^|\n)##\s+([^\n]+)\n([\s\S]*?)(?=(?:\n##\s+)|$)/g;
  let match = regex.exec(text);

  while (match) {
    sections.push({
      title: cleanInlineMarkdown(match[1]),
      content: match[2].trim(),
      contentText: cleanInlineMarkdown(match[2]),
    });
    match = regex.exec(text);
  }

  if (!sections.length) {
    return [
      {
        title: t('mk_fallbackSection'),
        content: text,
        contentText: cleanInlineMarkdown(text),
      },
    ];
  }

  return sections;
};

const findSectionContent = (sections, aliases) => {
  const loweredAliases = aliases.map((alias) => alias.toLowerCase());

  const section = sections.find((item) => {
    const title = item.title.toLowerCase();
    return loweredAliases.some((alias) => title.includes(alias));
  });

  return section || null;
};

const normalizeMetricEntries = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') {
          const [label, metricValue] = item.split(':');
          if (!metricValue) return null;
          return {
            label: cleanInlineMarkdown(label),
            value: cleanInlineMarkdown(metricValue),
          };
        }

        if (item && typeof item === 'object') {
          const label =
            item.label || item.name || item.metric || item.key || item.title;
          const metricValue = item.value ?? item.result ?? item.data;
          if (!label || metricValue === undefined || metricValue === null) {
            return null;
          }
          return {
            label: cleanInlineMarkdown(String(label)),
            value: cleanInlineMarkdown(String(metricValue)),
          };
        }

        return null;
      })
      .filter(Boolean);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value)
      .map(([key, metricValue]) => {
        if (
          metricValue === undefined ||
          metricValue === null ||
          metricValue === ''
        ) {
          return null;
        }
        return {
          label: toPrettyLabel(key),
          value: cleanInlineMarkdown(String(metricValue)),
        };
      })
      .filter(Boolean);
  }

  return [];
};

const parseMetricsFromSection = (section) => {
  if (!section) return [];

  return String(section.content || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => /^[-*]\s+/.test(line) && line.includes(':'))
    .map((line) => line.replace(/^[-*]\s+/, ''))
    .map((line) => {
      const separatorIndex = line.indexOf(':');
      const label = cleanInlineMarkdown(line.slice(0, separatorIndex));
      const value = cleanInlineMarkdown(line.slice(separatorIndex + 1));
      if (!label || !value) return null;
      return { label, value };
    })
    .filter(Boolean);
};

const inferSpeaker = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('cliente') || normalized.includes('customer'))
    return 'Cliente';
  if (normalized.includes('agente') || normalized.includes('agent'))
    return 'Agente';
  return null;
};

const findFieldByAlias = (source, aliases) => {
  if (!source || typeof source !== 'object') return undefined;
  const keys = Object.keys(source);

  for (const alias of aliases) {
    const exact = keys.find((key) => key.toLowerCase() === alias.toLowerCase());
    if (exact) return source[exact];
  }

  for (const alias of aliases) {
    const fuzzy = keys.find((key) =>
      key.toLowerCase().includes(alias.toLowerCase())
    );
    if (fuzzy) return source[fuzzy];
  }

  return undefined;
};

const normalizeTopicRows = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        const label = cleanInlineMarkdown(
          stripNumericPrefix(String(item || ''))
        );
        if (!label) return null;
        return { rank: index + 1, topic: label };
      }

      const topic = findFieldByAlias(item, [
        'topic',
        'tema',
        'word',
        'palabra',
        'phrase',
        'frase',
        'label',
        'name',
        'item',
      ]);
      const count = findFieldByAlias(item, [
        'count',
        'conteo',
        'occurrences',
        'ocurrencias',
        'frequency',
      ]);
      const percentage = findFieldByAlias(item, [
        'percentage',
        'porcentaje',
        'pct',
        'share',
      ]);
      const example = findFieldByAlias(item, [
        'example',
        'ejemplo',
        'evidence',
        'quote',
        'fraseEjemplo',
      ]);
      const context = findFieldByAlias(item, ['context', 'motivo', 'reason']);
      const problem = findFieldByAlias(item, [
        'problem',
        'issue',
        'pain',
        'problema',
      ]);
      const betterReply = findFieldByAlias(item, [
        'betterResponse',
        'recommendedResponse',
        'idealResponse',
      ]);
      const speakerRaw = findFieldByAlias(item, ['speaker', 'role', 'actor']);
      const speaker = inferSpeaker(speakerRaw) || inferSpeaker(topic);

      const normalizedTopic = cleanInlineMarkdown(String(topic || ''));
      if (!normalizedTopic) return null;

      return {
        rank: index + 1,
        topic: normalizedTopic,
        count:
          count === undefined || count === null || count === ''
            ? null
            : cleanInlineMarkdown(String(count)),
        percentage:
          percentage === undefined || percentage === null || percentage === ''
            ? null
            : cleanInlineMarkdown(String(percentage)).replace(/(?<!%)$/, '%'),
        example:
          example === undefined || example === null || example === ''
            ? null
            : cleanInlineMarkdown(String(example)),
        context:
          context === undefined || context === null || context === ''
            ? null
            : cleanInlineMarkdown(String(context)),
        problem:
          problem === undefined || problem === null || problem === ''
            ? null
            : cleanInlineMarkdown(String(problem)),
        betterReply:
          betterReply === undefined ||
          betterReply === null ||
          betterReply === ''
            ? null
            : cleanInlineMarkdown(String(betterReply)),
        speaker,
      };
    })
    .filter(Boolean);
};

const parseTopicRowsFromMetricLines = (metricEntries) => {
  return metricEntries
    .map((entry, index) => {
      const normalizedValue = String(entry.value || '');
      const match = normalizedValue.match(/(\d+)\s*\/\s*(\d+).*?([\d.,]+)\s*%/);

      if (!match) return null;

      return {
        rank: index + 1,
        topic: cleanInlineMarkdown(entry.label),
        count: `${match[1]}/${match[2]}`,
        percentage: `${match[3]}%`,
        speaker: inferSpeaker(entry.label),
      };
    })
    .filter(Boolean);
};

const pickBestTopicArrayFromObject = (source) => {
  if (!source || typeof source !== 'object') return [];

  const candidates = Object.entries(source)
    .filter(([, value]) => Array.isArray(value) && value.length > 0)
    .map(([key, value]) => {
      let score = 0;
      const lowerKey = key.toLowerCase();

      if (
        lowerKey.includes('top') ||
        lowerKey.includes('tema') ||
        lowerKey.includes('topic') ||
        lowerKey.includes('word') ||
        lowerKey.includes('phrase') ||
        lowerKey.includes('insight')
      ) {
        score += 4;
      }

      const firstItem = value[0];
      if (firstItem && typeof firstItem === 'object') {
        const keys = Object.keys(firstItem).map((item) => item.toLowerCase());
        if (
          keys.some(
            (item) => item.includes('count') || item.includes('porcentaje')
          )
        ) {
          score += 4;
        }
        if (
          keys.some((item) => item.includes('topic') || item.includes('tema'))
        ) {
          score += 3;
        }
      }

      return { key, score, value };
    })
    .sort((a, b) => b.score - a.score);

  if (!candidates.length) return [];
  return normalizeTopicRows(candidates[0].value);
};

const buildSpeakerBuckets = (topicRows, insightList) => {
  const buckets = {
    Cliente: [],
    Agente: [],
  };

  topicRows.forEach((row) => {
    if (row.speaker && buckets[row.speaker]) {
      buckets[row.speaker].push(row.topic);
    }
  });

  insightList.forEach((item) => {
    const speaker = inferSpeaker(item);
    if (speaker && buckets[speaker]) {
      buckets[speaker].push(item);
    }
  });

  return {
    clients: Array.from(new Set(buckets.Cliente)).slice(0, 5),
    agents: Array.from(new Set(buckets.Agente)).slice(0, 5),
  };
};

const getSampleAdvice = (prompt, rowsSentToAnalysis, currentMaxRows, t) => {
  const asksTopList = /\btop\s*(\d+|five|cinco)\b/i.test(prompt);
  const lowSample =
    Number.isFinite(rowsSentToAnalysis) && rowsSentToAnalysis <= 20;

  if (!asksTopList || !lowSample) return null;

  const suggestedMaxRows = Math.min(
    500,
    Math.max(180, normalizeMaxRows(currentMaxRows) * 2)
  );

  return {
    message: t('mk_sampleAdvice'),
    suggestedMaxRows,
  };
};

const normalizeRunResponse = (payload) => {
  const pipeline = payload?.pipeline || {};
  const ui = payload?.ui || {};

  const rowCountRaw = pipeline?.rowCount ?? payload?.rowCount;
  const rowsSentRaw =
    pipeline?.rowsSentToAnalysis ??
    payload?.rowsSentToAnalysis ??
    pipeline?.rowsSent;

  const rowCount = Number(rowCountRaw);
  const rowsSentToAnalysis = Number(rowsSentRaw);
  const analysisJsonObject =
    parseJsonValue(payload?.analysisJsonObject) ||
    parseJsonValue(pipeline?.analysisJsonObject) ||
    parseJsonValue(payload?.normalizedOutputJson) ||
    parseJsonValue(payload?.normalizedOutput) ||
    parseJsonValue(pipeline?.normalizedOutputJson) ||
    parseJsonValue(payload?.analysisJson) ||
    parseJsonValue(payload?.analysis) ||
    parseJsonValue(pipeline?.analysisJson) ||
    parseJsonValue(pipeline?.analysis) ||
    null;

  return {
    plan: parseJsonValue(pipeline?.plan) || pipeline?.plan || null,
    rowCount: Number.isFinite(rowCount) ? rowCount : null,
    rowsSentToAnalysis: Number.isFinite(rowsSentToAnalysis)
      ? rowsSentToAnalysis
      : null,
    analysisJson:
      parseJsonValue(pipeline?.analysisJson) ||
      parseJsonValue(pipeline?.analysis) ||
      parseJsonValue(payload?.analysisJson) ||
      parseJsonValue(payload?.analysis) ||
      null,
    analysisJsonObject,
    normalizedOutputJson: analysisJsonObject,
    promptParseRawOutput:
      parseJsonValue(pipeline?.promptParseRawOutput) ||
      pipeline?.promptParseRawOutput ||
      null,
    uiSpecJson: parseJsonValue(ui?.uiSpecJson),
    html: typeof ui?.html === 'string' && ui.html.trim() ? ui.html : null,
    jsx: typeof ui?.jsx === 'string' && ui.jsx.trim() ? ui.jsx : null,
    rawModelOutput: firstNonEmptyString(
      ui?.rawModelOutput,
      payload?.rawModelOutput,
      pipeline?.rawModelOutput
    ),
  };
};

const formatMetricValue = (value) => {
  const numeric = toNumber(value);
  if (!Number.isFinite(numeric)) return '--';
  return new Intl.NumberFormat('es-CL').format(numeric);
};

const formatPercentValue = (value) => {
  const numeric = toNumber(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 1)}%`;
};

const normalizeToken = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const FEELING_BUCKETS = {
  positive: ['positive', 'positivo', 'positiva'],
  neutral: ['neutral', 'neutro', 'neutra'],
  negative: ['negative', 'negativo', 'negativa'],
};

const findFeelingBucket = (value) => {
  const normalized = normalizeToken(value);
  if (!normalized) return null;

  const bucket = Object.entries(FEELING_BUCKETS).find(([, aliases]) =>
    aliases.some((alias) => normalized.includes(alias))
  );

  return bucket ? bucket[0] : null;
};

const isNumericLike = (value) => {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'string') return false;
  const normalized = value
    .trim()
    .replace('%', '')
    .replace(/\s+/g, '')
    .replace(',', '.');
  return normalized !== '' && Number.isFinite(Number(normalized));
};

const getKpiDisplayValue = (value) => {
  if (typeof value === 'string' && !isNumericLike(value)) return value.trim();
  return formatMetricValue(value);
};

const mergeFeelingKpiItems = (items, kpis, t) => {
  if (!Array.isArray(items) || !items.length) return items;

  const counts = {
    positive: 0,
    neutral: 0,
    negative: 0,
  };
  const consumedIds = new Set();
  let hasFeelingData = false;

  items.forEach((item, index) => {
    const bucket = findFeelingBucket(
      `${item?.id || index} ${item?.title || ''}`
    );
    if (!bucket) return;
    const value = toNumber(item?.value);
    counts[bucket] += value;
    consumedIds.add(item?.id || `kpi-${index + 1}`);
    hasFeelingData = true;
  });

  if (kpis && typeof kpis === 'object') {
    Object.entries(kpis).forEach(([key, rawValue]) => {
      const bucket = findFeelingBucket(key);
      if (!bucket) return;
      counts[bucket] += toNumber(rawValue);
      hasFeelingData = true;
    });
  }

  if (!hasFeelingData) return items;

  const labels = [];
  if (counts.positive > 0)
    labels.push(`${formatMetricValue(counts.positive)} ${t('mk_feelingPositive')}`);
  if (counts.neutral > 0)
    labels.push(`${formatMetricValue(counts.neutral)} ${t('mk_feelingNeutral')}`);
  if (counts.negative > 0)
    labels.push(`${formatMetricValue(counts.negative)} ${t('mk_feelingNegative')}`);

  const summaryValue = labels.length ? labels.join(' · ') : t('mk_feelingNoData');
  const baseItems = items.filter(
    (item, index) => !consumedIds.has(item?.id || `kpi-${index + 1}`)
  );

  return [
    ...baseItems,
    {
      id: 'call-feelings-summary',
      title: t('mk_feelingTitle'),
      value: summaryValue,
      unit: '',
      description: '',
    },
  ];
};

const extractEvidenceCallId = (value, t) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const rawCallId = value.call_id ?? value.callId ?? value.id ?? null;
    const callId =
      rawCallId === null || rawCallId === undefined || rawCallId === ''
        ? ''
        : String(rawCallId).trim();
    const text = firstNonEmptyString(
      value.text,
      value.content,
      value.description,
      value.label,
      ''
    );

    if (callId) {
      return {
        callId,
        text: text || t('mk_callDetail', { id: callId }),
        label: `ID ${callId}`,
      };
    }

    if (text) {
      return {
        callId: '',
        text,
        label: '',
      };
    }

    return null;
  }

  const text = String(value || '').trim();
  if (!text) return null;

  const match = text.match(/\bID\s*[:#-]?\s*(\d+)\b/i);
  if (!match) return null;

  const callId = match[1];
  const withoutId = text
    .replace(match[0], '')
    .replace(/^\s*[:\-–—]\s*/, '')
    .trim();

  return {
    callId,
    text: withoutId || text,
    label: `ID ${callId}`,
  };
};

const getKpiCardStatsConfig = (item) => {
  const token = normalizeToken(`${item?.id || ''} ${item?.title || ''}`);

  if (token.includes('excluded') || token.includes('excluid')) {
    return { type: 'error', icon: 'close', fill: false };
  }

  if (
    token.includes('usable') ||
    token.includes('util') ||
    token.includes('complet')
  ) {
    return { type: 'success', icon: 'check2Circle', fill: true };
  }

  if (
    token.includes('feeling') ||
    token.includes('sentim') ||
    token.includes('share') ||
    token.includes('tasa')
  ) {
    return { type: 'warning', icon: 'infoCircle', fill: false };
  }

  return { type: 'info', icon: 'clipboard', fill: false };
};

const buildDefaultKpiItems = (kpis, t) => {
  if (!kpis || typeof kpis !== 'object') return [];
  const map = [
    { key: 'sample_size', title: t('mk_kpiSample') },
    { key: 'usable_calls', title: t('mk_kpiUsable') },
    { key: 'excluded_calls', title: t('mk_kpiExcluded') },
  ];

  return map
    .filter((item) => kpis[item.key] !== undefined && kpis[item.key] !== null)
    .map((item) => ({
      id: item.key,
      title: item.title,
      value: kpis[item.key],
      unit: '',
      description: '',
    }));
};

const buildPrebuiltTemplateModel = ({ result, fallbackPrompt, t }) => {
  const source =
    parseJsonValue(result?.analysisJsonObject) ||
    parseJsonValue(result?.normalizedOutputJson) ||
    parseJsonValue(result?.analysisJson) ||
    null;

  if (!source || typeof source !== 'object') return null;

  const header =
    source?.header && typeof source.header === 'object' ? source.header : {};
  const kpis =
    source?.kpis && typeof source.kpis === 'object' ? source.kpis : {};
  const kpiItemsRaw = Array.isArray(source?.kpi_items) ? source.kpi_items : [];
  const kpiItemsMapped = (
    kpiItemsRaw.length ? kpiItemsRaw : buildDefaultKpiItems(kpis, t)
  )
    .map((item, index) => ({
      id: firstNonEmptyString(item?.id, `kpi-${index + 1}`),
      title: firstNonEmptyString(item?.title, `KPI ${index + 1}`),
      value: item?.value,
      unit: firstNonEmptyString(item?.unit, ''),
      description: firstNonEmptyString(item?.description, ''),
    }))
    .filter((item) => item.value !== undefined && item.value !== null);
  const CORE_KPI_KEYS = ['sample_size', 'usable_calls', 'excluded_calls'];
  const kpiItemsById = new Map(
    kpiItemsMapped.map((item) => [normalizeToken(item.id), item])
  );

  const primaryKpiItems = CORE_KPI_KEYS.filter(
    (key) => kpis[key] !== undefined && kpis[key] !== null
  ).map((key) => {
    const configured = kpiItemsById.get(normalizeToken(key));
    return {
      id: key,
      title: configured?.title || toPrettyLabel(key),
      value: kpis[key],
      unit: configured?.unit || '',
      description: configured?.description || '',
    };
  });

  const secondaryKpiItemsBase = kpiItemsMapped.filter(
    (item) => !CORE_KPI_KEYS.includes(normalizeToken(item.id))
  );
  const secondaryKpiItems = mergeFeelingKpiItems(secondaryKpiItemsBase, kpis, t);
  const allKpiItems = [...primaryKpiItems, ...secondaryKpiItems];

  const chartsRaw = Array.isArray(source?.charts) ? source.charts : [];
  const charts = chartsRaw
    .map((chart) => ({
      id: chart?.id || createHistoryId(),
      title: chart?.title || t('mk_chartTitle'),
      chart_type: normalizeChartType(chart?.chart_type || chart?.type || 'bar'),
      data: Array.isArray(chart?.data) ? chart.data : [],
    }))
    .filter((chart) => chart.data.length > 0);

  const topFindings = Array.isArray(source?.top_findings)
    ? source.top_findings
    : [];
  const recommendations = Array.isArray(source?.recommended_actions)
    ? [...source.recommended_actions].sort(
        (a, b) => toNumber(a?.priority) - toNumber(b?.priority)
      )
    : [];
  const limitations = Array.isArray(source?.limitations)
    ? source.limitations
    : [];

  const hasData =
    charts.length > 0 ||
    topFindings.length > 0 ||
    recommendations.length > 0 ||
    allKpiItems.length > 0;

  if (!hasData) return null;

  return {
    title: firstNonEmptyString(
      header?.title,
      source?.report_title,
      t('mk_reportDefault')
    ),
    subtitle: firstNonEmptyString(
      header?.subtitle,
      source?.subtitle,
      fallbackPrompt,
      ''
    ),
    description: firstNonEmptyString(header?.description, ''),
    answer: firstNonEmptyString(source?.answer, ''),
    dateRange: {
      from: firstNonEmptyString(
        header?.date_from,
        source?.date_range?.from,
        ''
      ),
      to: firstNonEmptyString(header?.date_to, source?.date_range?.to, ''),
    },
    kpis,
    primaryKpiItems,
    secondaryKpiItems,
    kpiItems: allKpiItems,
    topFindings,
    charts,
    recommendations,
    confidence: firstNonEmptyString(
      source?.confidence,
      header?.trustworthiness,
      ''
    ),
    trustworthiness: firstNonEmptyString(
      source?.trustworthiness,
      header?.trustworthiness,
      ''
    ),
    limitations,
  };
};

const buildDynamicModel = ({ result, prompt, currentMaxRows, t }) => {
  const baseObject =
    result.analysisJsonObject ||
    result.normalizedOutputJson ||
    result.analysisJson ||
    result.uiSpecJson ||
    null;

  const sections = parseMarkdownSections(result.rawModelOutput, t);
  const summarySection = findSectionContent(sections, SECTION_MATCHERS.summary);
  const insightsSection = findSectionContent(
    sections,
    SECTION_MATCHERS.insights
  );
  const metricsSection = findSectionContent(sections, SECTION_MATCHERS.metrics);
  const evidenceSection = findSectionContent(
    sections,
    SECTION_MATCHERS.evidence
  );
  const recommendationsSection = findSectionContent(
    sections,
    SECTION_MATCHERS.recommendations
  );
  const confidenceSection = findSectionContent(
    sections,
    SECTION_MATCHERS.confidence
  );
  const limitationsSection = findSectionContent(
    sections,
    SECTION_MATCHERS.limitations
  );
  const stepsSection = findSectionContent(sections, SECTION_MATCHERS.steps);

  const summary = firstNonEmptyString(
    cleanInlineMarkdown(
      findFieldByAlias(baseObject, ['summary', 'resumen', 'directAnswer']) || ''
    ),
    summarySection?.contentText || ''
  );

  const metrics = [
    ...normalizeMetricEntries(
      findFieldByAlias(baseObject, ['metrics', 'metricas'])
    ),
    ...parseMetricsFromSection(metricsSection),
  ];

  const uniqueMetrics = Array.from(
    new Map(
      metrics.map((item) => [`${item.label}-${item.value}`, item])
    ).values()
  );

  const insightList = [
    ...normalizeTopicRows(
      findFieldByAlias(baseObject, ['insights', 'keyInsights'])
    ).map((item) => item.topic),
    ...toListFromMarkdownSection(insightsSection?.content),
  ].filter(Boolean);

  const uniqueInsights = Array.from(
    new Set(insightList.map(cleanInlineMarkdown))
  );

  let topicRows = normalizeTopicRows(
    findFieldByAlias(baseObject, [
      'topWords',
      'topTopics',
      'rankedItems',
      'top',
    ])
  );

  if (!topicRows.length) {
    topicRows = pickBestTopicArrayFromObject(baseObject);
  }

  if (!topicRows.length) {
    topicRows = parseTopicRowsFromMetricLines(uniqueMetrics);
  }

  if (!topicRows.length && uniqueInsights.length) {
    topicRows = uniqueInsights.map((item, index) => ({
      rank: index + 1,
      topic: cleanInlineMarkdown(stripNumericPrefix(item)),
    }));
  }

  const evidenceExamples = [
    ...normalizeTopicRows(
      findFieldByAlias(baseObject, ['evidence', 'examples'])
    ).map((item) => item.example || item.topic),
    ...toListFromMarkdownSection(evidenceSection?.content),
  ].filter(Boolean);

  const recommendations = [
    ...normalizeTopicRows(
      findFieldByAlias(baseObject, ['recommendations', 'recommendedActions'])
    ).map((item) => item.topic),
    ...toListFromMarkdownSection(recommendationsSection?.content),
  ].filter(Boolean);

  const steps = [
    ...normalizeTopicRows(
      findFieldByAlias(baseObject, ['steps', 'nextSteps'])
    ).map((item) => item.topic),
    ...toListFromMarkdownSection(stepsSection?.content),
  ].filter(Boolean);

  const limitations = [
    ...normalizeTopicRows(
      findFieldByAlias(baseObject, ['limitations', 'dataLimitations'])
    ).map((item) => item.topic),
    ...toListFromMarkdownSection(limitationsSection?.content),
  ].filter(Boolean);

  const confidence = firstNonEmptyString(
    cleanInlineMarkdown(
      findFieldByAlias(baseObject, ['confidence', 'confidenceLevel']) || ''
    ),
    confidenceSection?.contentText || ''
  );

  const speakerSplit = buildSpeakerBuckets(topicRows, uniqueInsights);

  const sampleAdvice = getSampleAdvice(
    prompt,
    result.rowsSentToAnalysis,
    currentMaxRows,
    t
  );

  const consumedSectionTitles = new Set(
    [
      summarySection,
      insightsSection,
      metricsSection,
      evidenceSection,
      recommendationsSection,
      confidenceSection,
      limitationsSection,
      stepsSection,
    ]
      .filter(Boolean)
      .map((item) => item.title)
  );

  const extraSections = sections
    .filter((item) => !consumedSectionTitles.has(item.title))
    .map((item) => ({
      title: item.title,
      text: item.contentText,
      list: toListFromMarkdownSection(item.content),
    }))
    .filter((item) => item.text || item.list.length);

  return {
    summary,
    metrics: uniqueMetrics,
    topicRows,
    insights: uniqueInsights,
    recommendations,
    steps,
    evidenceExamples,
    limitations,
    confidence,
    speakerSplit,
    sampleAdvice,
    extraSections,
  };
};

const readHistory = () => {
  if (typeof window === 'undefined') return [];

  const parseStored = (raw) => {
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => {
          const prompt = normalizePrompt(item?.prompt);
          if (!prompt) return null;

          return {
            id: item?.id || createHistoryId(),
            prompt,
            createdAt: item?.createdAt || new Date().toISOString(),
          };
        })
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  };

  const current = parseStored(localStorage.getItem(HISTORY_KEY));
  if (current.length) return current;

  return parseStored(localStorage.getItem(LEGACY_HISTORY_KEY));
};

const persistHistory = (history) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
};

const Surface = ({ Card, className = '', children }) => {
  if (Card) {
    return (
      <Card specialPadding='px-4 pt-4 pb-4' className={className}>
        {children}
      </Card>
    );
  }

  return (
    <div className={`marketing-analysis-surface ${className}`}>{children}</div>
  );
};

const renderButton = ({
  Button,
  label,
  onClick,
  disabled,
  variant = 'primary',
}) => {
  if (Button) {
    return (
      <Button
        variant={variant}
        size='sm'
        label={label}
        disabled={disabled}
        onClick={onClick}
      />
    );
  }

  return (
    <button
      type='button'
      className={`btn btn-sm ${variant === 'primary' ? 'btn-primary' : 'btn-outline-secondary'}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );
};

const AnalysisQualityCard = ({ model, t }) => {
  if (!model || (!model.confidence && model.limitations.length === 0))
    return null;

  return (
    <div className='marketing-fallback-card mb-3'>
      <h4 className='fw-semibold mb-2'>{t('mk_qualityTitle')}</h4>
      {model.confidence ? (
        <p className='mb-2'>{t('mk_qualityConfidence', { value: model.confidence })}</p>
      ) : null}
      {model.limitations.length > 0 ? (
        <ul className='marketing-list mb-0'>
          {model.limitations.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const PrebuiltReportTemplate = ({ model, DesignSystemComponents, t }) => {
  if (!model) return null;
  const Badge = DesignSystemComponents?.Badge;
  const canUseCardStats = Boolean(
    DesignSystemComponents?.Card &&
      DesignSystemComponents?.SvgIcon &&
      DesignSystemComponents?.Skeleton
  );
  const primaryKpiItems = Array.isArray(model.primaryKpiItems)
    ? model.primaryKpiItems
    : [];
  const secondaryKpiItems = Array.isArray(model.secondaryKpiItems)
    ? model.secondaryKpiItems
    : [];
  const hasKpis = primaryKpiItems.length > 0 || secondaryKpiItems.length > 0;

  const renderKpiCards = (items, rowKey) => {
    if (!items.length) return null;

    if (canUseCardStats) {
      return (
        <div className='row g-3'>
          {items.map((item) => {
            const statsConfig = getKpiCardStatsConfig(item);
            return (
              <div
                key={`${rowKey}-${item.id}`}
                className='col-12 col-md col-lg'
              >
                <CardStats
                  DesignSystemComponents={DesignSystemComponents}
                  title={item.title}
                  count={`${getKpiDisplayValue(item.value)}`}
                  subtitle={item.description || t('mk_kpiTotal')}
                  icon={statsConfig.icon}
                  type={statsConfig.type}
                  fill={statsConfig.fill}
                />
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className='marketing-report-kpi-grid'>
        {items.map((item) => (
          <div
            key={`${rowKey}-${item.id}`}
            className='marketing-report-kpi-item'
          >
            <p className='caption mb-1'>{item.title}</p>
            <p className='marketing-kpi-value mb-0'>
              {getKpiDisplayValue(item.value)}
              {item.unit ? ` ${item.unit}` : ''}
            </p>
            {item.description ? (
              <p className='caption mb-0 mt-1'>{item.description}</p>
            ) : null}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className='marketing-prebuilt-report mb-3 gap-4'>
      <section className='marketing-report-header'>
        <h3 className='marketing-report-header-title mb-3 fw-bold'>
          {model.title}
        </h3>
        <div className='marketing-report-header-lines'>
          {model.subtitle ? (
            <p className='marketing-report-header-description mb-0 caption'>
              {model.subtitle}
            </p>
          ) : null}
          {model.description ? (
            <p className='marketing-report-header-description mb-0 caption'>
              {model.description}
            </p>
          ) : null}
          {model.answer ? (
            <p className='marketing-report-header-description mb-0 caption'>
              {model.answer}
            </p>
          ) : null}
        </div>
      </section>

      {hasKpis ? (
        <section>
          {renderKpiCards(primaryKpiItems, 'kpi-core')}
          {secondaryKpiItems.length > 0 ? (
            <div className='mt-3'>
              {renderKpiCards(secondaryKpiItems, 'kpi-extra')}
            </div>
          ) : null}
        </section>
      ) : null}

      {model.charts.length > 0 ? (
        <section>
          <div
            className={`marketing-report-chart-grid ${
              model.charts.length === 1
                ? 'is-single'
                : model.charts.length === 2
                  ? 'is-double'
                  : 'is-multi'
            }`}
          >
            {model.charts.map((chart) => (
              <div key={chart.id} className='marketing-report-chart-item'>
                <h4 className='mb-3'>{chart.title}</h4>
                <InlineChart component={chart} t={t} />
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {model.topFindings.length > 0 ? (
        <section>
          <h4 className='fw-semibold mb-3'>{t('mk_findingsTitle')}</h4>
          <div className='marketing-report-analysis-list'>
            {model.topFindings.map((finding, index) => (
              <article
                key={`finding-${finding.rank || index + 1}`}
                className='marketing-report-analysis-item'
              >
                <div className='marketing-analysis-heading mb-2'>
                  <span className='marketing-analysis-rank'>
                    {finding.rank || index + 1}
                  </span>
                  <p className='fw-semibold caption mb-0 marketing-analysis-theme'>
                    {finding.theme}
                  </p>
                </div>
                <div className='d-flex align-items-center gap-2 mb-2'>
                  <p className='caption mb-0'>
                    {t('mk_findingsCalls', { count: formatMetricValue(finding.count) })}
                  </p>
                  {formatPercentValue(finding.percentage) ? (
                    Badge ? (
                      <Badge
                        text={formatPercentValue(finding.percentage)}
                        color='neutral'
                        size='sm'
                      />
                    ) : (
                      <span className='marketing-analysis-percentage-badge'>
                        {formatPercentValue(finding.percentage)}
                      </span>
                    )
                  ) : null}
                </div>
                {Array.isArray(finding.evidence) &&
                finding.evidence.length > 0 ? (
                  <ul className='marketing-list mb-0'>
                    {finding.evidence.slice(0, 2).map((item, index) => {
                      const evidence = extractEvidenceCallId(item, t);
                      return (
                        <li
                          key={`${finding.rank}-evidence-${index}`}
                          className='marketing-evidence-card caption'
                        >
                          {evidence?.callId ? (
                            <>
                              <Link
                                to={`/detalles-llamada/${evidence.callId}`}
                                className='marketing-evidence-link'
                              >
                                {evidence.label}
                              </Link>
                              {`: ${evidence.text}`}
                            </>
                          ) : evidence?.text ? (
                            evidence.text
                          ) : (
                            String(item || '')
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {model.recommendations.length > 0 ? (
        <section className='marketing-fallback-card'>
          <h4 className='fw-semibold mb-3'>{t('mk_recommendationsTitle')}</h4>
          <div className='marketing-report-recommendations'>
            {model.recommendations.map((recommendation, index) => (
              <article
                key={`recommendation-${recommendation.priority || index}`}
                className='marketing-report-recommendation-item'
              >
                <p className='fw-semibold mb-1'>
                  P{recommendation.priority || index + 1}
                </p>
                <p className='mb-1'>{recommendation.action}</p>
                {recommendation.expected_impact ? (
                  <p className='caption mb-0'>
                    {t('mk_expectedImpact', { impact: recommendation.expected_impact })}
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className='marketing-fallback-card'>
        <h4 className='fw-semibold mb-2'>{t('mk_confidenceTitle')}</h4>
        <p className='mb-2'>{model.confidence || '--'}</p>
        {model.trustworthiness ? (
          <p className='caption mb-2'>
            {t('mk_trustworthiness', { value: model.trustworthiness })}
          </p>
        ) : null}
        {model.limitations.length > 0 ? (
          <ul className='marketing-list mb-0'>
            {model.limitations.map((item, index) => (
              <li key={`limitation-${index}`}>{item}</li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
};

const MarketingAnalysis = ({ DesignSystemComponents }) => {
  const { Card, Button } = DesignSystemComponents || {};

  const { language } = useLanguage();
  const mkLanguage = normalizeSupportedLanguage(language);

  if (marketingI18n.language !== mkLanguage) {
    marketingI18n.changeLanguage(mkLanguage);
  }

  const { t } = useTranslation(undefined, { i18n: marketingI18n });

  const quickPrompts = useMemo(() => getQuickPrompts(t), [mkLanguage]);

  const requestRef = useRef(null);
  const analysisLoaderRef = useRef(null);

  const [prompt, setPrompt] = useState('');
  const [maxRows, setMaxRows] = useState(DEFAULT_MAX_ROWS);
  const [timeZoneId] = useState(() => getClientTimeZone());

  const [history, setHistory] = useState(() => readHistory());
  const [activeSidebarTab, setActiveSidebarTab] = useState('new');
  const [activeEntryId, setActiveEntryId] = useState(null);

  const [activeResult, setActiveResult] = useState(null);
  const [resultCache, setResultCache] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [hasLottieLoader, setHasLottieLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    persistHistory(history);
  }, [history]);

  useEffect(() => {
    return () => {
      if (requestRef.current) {
        requestRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!isLoading) return undefined;

    let animation = null;
    let cancelled = false;
    const loaderEl = analysisLoaderRef.current;
    setHasLottieLoader(false);

    loadLottiePlayer()
      .then((lottie) => {
        if (cancelled || !lottie || !loaderEl) return;
        animation = lottie.loadAnimation({
          container: loaderEl,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: loaderSpinnerAnimation,
        });
        setHasLottieLoader(true);
      })
      .catch(() => {
        setHasLottieLoader(false);
      });

    return () => {
      cancelled = true;
      if (animation) animation.destroy();
      if (loaderEl?.classList?.contains('marketing-analysis-loading-spinner')) {
        loaderEl.innerHTML = '';
      }
    };
  }, [isLoading]);

  const activeHistoryEntry = useMemo(
    () => history.find((entry) => entry.id === activeEntryId) || null,
    [activeEntryId, history]
  );

  const activeModel = useMemo(() => {
    if (!activeResult) return null;

    return buildDynamicModel({
      result: activeResult,
      prompt: activeHistoryEntry?.prompt || prompt,
      currentMaxRows: maxRows,
      t,
    });
  }, [activeHistoryEntry?.prompt, activeResult, maxRows, prompt, t]);

  const prebuiltTemplateModel = useMemo(
    () =>
      buildPrebuiltTemplateModel({
        result: activeResult,
        fallbackPrompt: activeHistoryEntry?.prompt || prompt,
        t,
      }),
    [activeHistoryEntry?.prompt, activeResult, prompt, t]
  );

  const executeReport = async (
    rawPrompt,
    { entryId = null, writeHistory = true, overrideMaxRows = null } = {}
  ) => {
    const normalizedPrompt = normalizePrompt(rawPrompt);
    if (!normalizedPrompt) return;

    if (requestRef.current) {
      requestRef.current.abort();
    }

    const controller = new AbortController();
    requestRef.current = controller;

    setIsLoading(true);
    setErrorMessage('');

    try {
      const rowsForRequest = normalizeMaxRows(overrideMaxRows ?? maxRows);

      const response = await runMarketingReport(
        {
          userPrompt: normalizedPrompt,
          timeZoneId,
          maxRows: rowsForRequest,
        },
        controller.signal
      );

      const normalized = normalizeRunResponse(response);
      const selectedEntryId = entryId || createHistoryId();

      setActiveResult(normalized);
      setResultCache((prev) => ({
        ...prev,
        [selectedEntryId]: normalized,
      }));
      setActiveEntryId(selectedEntryId);

      if (writeHistory) {
        setHistory((previous) => {
          const deduped = previous.filter(
            (entry) =>
              entry.id !== selectedEntryId &&
              entry.prompt.toLowerCase() !== normalizedPrompt.toLowerCase()
          );

          return [
            {
              id: selectedEntryId,
              prompt: normalizedPrompt,
              createdAt: new Date().toISOString(),
            },
            ...deduped,
          ].slice(0, MAX_HISTORY);
        });
      }

      if (overrideMaxRows) {
        setMaxRows(rowsForRequest);
      }

      setPrompt('');
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return;
      }

      setErrorMessage(
        error?.customMessage ||
          error?.message ||
          t('mk_errorDefault')
      );
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
      }
      setIsLoading(false);
    }
  };

  const handleSelectHistory = (entry) => {
    setActiveEntryId(entry.id);

    const cached = resultCache[entry.id];
    if (cached) {
      setErrorMessage('');
      setActiveResult(cached);
      return;
    }

    executeReport(entry.prompt, { entryId: entry.id, writeHistory: false });
  };

  const handleIncreaseSample = () => {
    if (!activeModel?.sampleAdvice) return;

    const targetPrompt = activeHistoryEntry?.prompt || prompt;
    if (!normalizePrompt(targetPrompt)) return;

    executeReport(targetPrompt, {
      entryId: activeEntryId,
      writeHistory: false,
      overrideMaxRows: activeModel.sampleAdvice.suggestedMaxRows,
    });
  };

  const onPromptKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      executeReport(prompt);
    }
  };

  return (
    <div className='marketing-analysis-page'>
      <div className='mb-4'>
        <h2 className='fw-semibold mb-1'>{t('mk_pageTitle')}</h2>
        <h4 className='fw-normal mb-0'>
          {t('mk_pageSubtitle')}
        </h4>
      </div>

      <div className='marketing-analysis-layout'>
        <div className='marketing-analysis-sidebar d-flex flex-column gap-3'>
          <Surface Card={Card}>
            <div className='marketing-sidebar-tabs mb-3'>
              <button
                type='button'
                className={`marketing-sidebar-tab ${activeSidebarTab === 'new' ? 'is-active' : ''}`}
                onClick={() => setActiveSidebarTab('new')}
                aria-pressed={activeSidebarTab === 'new'}
              >
                {t('mk_tabNew')}
              </button>
              <button
                type='button'
                className={`marketing-sidebar-tab ${activeSidebarTab === 'history' ? 'is-active' : ''}`}
                onClick={() => setActiveSidebarTab('history')}
                aria-pressed={activeSidebarTab === 'history'}
              >
                {t('mk_tabHistory', { count: history.length })}
              </button>
            </div>

            {activeSidebarTab === 'new' ? (
              <>
                <p className='caption mb-3'>
                  {t('mk_sidebarDescription')}
                </p>

                <div className='marketing-controls-grid mb-3'>
                  <div>
                    <p className='caption mb-1'>{t('mk_timezoneLabel')}</p>
                    <div className='marketing-static-field'>{timeZoneId}</div>
                  </div>
                  <div>
                    <label
                      htmlFor='marketing-max-rows'
                      className='caption mb-1 d-block'
                    >
                      {t('mk_maxRowsLabel')}
                    </label>
                    <input
                      id='marketing-max-rows'
                      type='number'
                      className='form-control'
                      min={1}
                      max={500}
                      step={1}
                      value={maxRows}
                      onChange={(event) =>
                        setMaxRows(normalizeMaxRows(event.target.value))
                      }
                    />
                  </div>
                </div>

                <textarea
                  className='form-control marketing-analysis-textarea mb-3'
                  rows={5}
                  value={prompt}
                  placeholder={t('mk_textareaPlaceholder')}
                  onChange={(event) => setPrompt(event.target.value)}
                  onKeyDown={onPromptKeyDown}
                />

                <div className='d-flex justify-content-end mb-3'>
                  {renderButton({
                    Button,
                    label: isLoading ? t('mk_btnGenerating') : t('mk_btnGenerate'),
                    onClick: () => executeReport(prompt),
                    disabled: !normalizePrompt(prompt) || isLoading,
                  })}
                </div>

                <div className='d-flex flex-wrap gap-2'>
                  {quickPrompts.map((quickPrompt) => (
                    <button
                      key={quickPrompt}
                      type='button'
                      className='marketing-quick-prompt'
                      onClick={() => executeReport(quickPrompt)}
                      disabled={isLoading}
                    >
                      {quickPrompt}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className='marketing-history-list'>
                {!history.length && (
                  <p className='caption mb-0'>
                    {t('mk_historyEmpty')}
                  </p>
                )}

                {history.map((entry) => {
                  const isActive = entry.id === activeHistoryEntry?.id;
                  return (
                    <button
                      key={entry.id}
                      type='button'
                      className={`marketing-history-item ${isActive ? 'is-active' : ''}`}
                      onClick={() => handleSelectHistory(entry)}
                      disabled={isLoading}
                    >
                      <span className='marketing-history-prompt'>
                        {entry.prompt}
                      </span>
                      <span className='caption'>
                        {new Date(entry.createdAt).toLocaleString()}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </Surface>
        </div>

        <div className='marketing-analysis-main'>
          <Surface Card={Card}>
            {isLoading && (
              <div className='marketing-empty-state marketing-analysis-loading'>
                <div
                  ref={analysisLoaderRef}
                  className='marketing-analysis-loading-spinner'
                  aria-hidden='true'
                />
                {!hasLottieLoader && (
                  <div
                    className='spinner-border text-primary'
                    role='status'
                    aria-hidden='true'
                  />
                )}
                <p className='marketing-empty-title mb-2'>{t('mk_loadingTitle')}</p>
                <p className='caption mb-0'>
                  {t('mk_loadingDescription')}
                </p>
              </div>
            )}

            {!isLoading && errorMessage && (
              <div className='marketing-error-state'>
                <p className='marketing-empty-title mb-2'>{t('mk_errorTitle')}</p>
                <p className='mb-0'>{errorMessage}</p>
              </div>
            )}

            {!isLoading && !errorMessage && !activeResult && (
              <div className='marketing-empty-state'>
                <p className='marketing-empty-title mb-2'>
                  {t('mk_emptyTitle')}
                </p>
                <p className='caption mb-0'>
                  {t('mk_emptyDescription')}
                </p>
              </div>
            )}

            {!isLoading && !errorMessage && activeResult && (
              <>
                {activeModel?.sampleAdvice && (
                  <div className='marketing-sample-advice mb-3'>
                    <p className='mb-2'>{activeModel.sampleAdvice.message}</p>
                    {renderButton({
                      Button,
                      variant: 'secondary',
                      label: t('mk_expandSample', { rows: activeModel.sampleAdvice.suggestedMaxRows }),
                      onClick: handleIncreaseSample,
                      disabled: isLoading,
                    })}
                  </div>
                )}

                {prebuiltTemplateModel ? (
                  <PrebuiltReportTemplate
                    model={prebuiltTemplateModel}
                    DesignSystemComponents={DesignSystemComponents}
                    t={t}
                  />
                ) : (
                  <div className='marketing-component-empty mb-3'>
                    <p className='caption mb-0'>
                      {t('mk_noTemplate')}
                    </p>
                  </div>
                )}

                {!prebuiltTemplateModel && activeModel ? (
                  <AnalysisQualityCard model={activeModel} t={t} />
                ) : null}

                <div className='marketing-traceability mt-4'>
                  <div className='d-flex flex-wrap gap-3 mb-2'>
                    <span className='caption'>
                      {t('mk_rowCount')}: {activeResult.rowCount ?? '--'}
                    </span>
                    <span className='caption'>
                      {t('mk_rowsSent')}:{' '}
                      {activeResult.rowsSentToAnalysis ?? '--'}
                    </span>
                  </div>

                  <details>
                    <summary>{t('mk_pipelinePlan')}</summary>
                    <pre className='marketing-code-block'>
                      {JSON.stringify(activeResult.plan, null, 2)}
                    </pre>
                  </details>

                  <details>
                    <summary>{t('mk_pipelineAnalysis')}</summary>
                    <pre className='marketing-code-block'>
                      {JSON.stringify(activeResult.analysisJson, null, 2)}
                    </pre>
                  </details>

                  <details>
                    <summary>{t('mk_rawOutput')}</summary>
                    <pre className='marketing-code-block'>
                      {activeResult.rawModelOutput || '--'}
                    </pre>
                  </details>
                </div>
              </>
            )}
          </Surface>
        </div>
      </div>
    </div>
  );
};

export default MarketingAnalysis;
